import { LoadingOverlay, Modal } from '@mantine/core';
import { reduce } from 'lodash/fp';
import React, { useMemo } from 'react';

import {
  useActiveConnectionsResponse,
  useCreateOrganization,
  UseCreateOrganizationParams,
  useIsChannelPartner,
} from '@portals/api/partners';
import { AutoFormik } from '@portals/autoformik';
import { ModalProps, useHasSupportSeat } from '@portals/framework';
import { FieldType, FieldTypeEnum } from '@portals/types';

const BASE_FIELDS: Array<FieldType> = [
  {
    name: 'organization_name',
    title: 'Customer organization name',
    required: true,
  },
  {
    name: 'name',
    title: 'Customer admin name',
    required: true,
  },
  {
    name: 'email',
    title: 'Customer admin email',
    required: true,
    type: FieldTypeEnum.Email,
  },
];

interface AddOrganizationProps extends ModalProps {}

export function AddOrganization({ closeMe }: AddOrganizationProps) {
  const hasSupportSeat = useHasSupportSeat();

  const createOrganization = useCreateOrganization();

  const isChannelPartner = useIsChannelPartner();

  const activeConnectionsResponse = useActiveConnectionsResponse({
    queryOptions: {
      enabled: isChannelPartner,
    },
  });

  const handleSubmit = async (data: UseCreateOrganizationParams) => {
    try {
      await createOrganization.mutateAsync(data);

      closeMe();
    } catch (e) {
      console.error(e);
    }
  };

  const fields = useMemo(() => {
    let fields: Array<FieldType>;

    if (isChannelPartner && activeConnectionsResponse.isFetched) {
      fields = [
        {
          name: 'main_partner_id',
          title: 'Connected partners',
          required: true,
          type: FieldTypeEnum.Select,
          options: reduce(
            (acc, curr) => {
              acc[curr.partner_id] = curr.display_name;
              return acc;
            },
            {},
            activeConnectionsResponse?.data?.data
          ),
        },
        ...BASE_FIELDS,
      ];
    } else {
      fields = [...BASE_FIELDS];
    }

    if (hasSupportSeat) {
      fields.push({
        name: 'auto_admin',
        title: 'Add me as admin to this customer',
        type: FieldTypeEnum.Checkbox,
        default: true,
      });
    }

    return fields;
  }, [
    isChannelPartner,
    activeConnectionsResponse.isFetched,
    activeConnectionsResponse?.data?.data,
    hasSupportSeat,
  ]);

  return (
    <Modal opened onClose={closeMe} title="Create Customer">
      <LoadingOverlay
        visible={
          activeConnectionsResponse.isFetching || createOrganization.isLoading
        }
      />

      <AutoFormik
        fields={fields}
        handleSubmit={handleSubmit}
        modal={closeMe}
        inProgress={createOrganization.isLoading}
        submitTitle="Create"
      />
    </Modal>
  );
}
